import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import {
  subscribeInstall,
  canInstall,
  promptInstall,
  isStandalone,
  isIosSafari,
} from '@/lib/pwaInstall';
import styles from './InstallAppButton.module.css';

/**
 * Unobtrusive "Install app" affordance for the driver PWA.
 *
 *   - Android / desktop Chromium: shows a button once the browser has
 *     offered an install prompt; clicking fires the native prompt.
 *   - iOS Safari: no prompt event exists, so shows a short Share-sheet
 *     hint instead.
 *   - Already installed (standalone) or no install path: renders nothing.
 *
 * Safe to drop anywhere — it self-hides. Naming is intentionally distinct
 * from AppShell's FULLSCREEN_ROUTES layout concept.
 */
export function InstallAppButton() {
  const { t } = useTranslation('chrome');
  const installable = useSyncExternalStore(subscribeInstall, canInstall, () => false);

  // Already launched as an installed app — nothing to offer.
  if (isStandalone()) return null;

  if (installable) {
    return (
      <div className={styles.installWrap}>
        <button
          type="button"
          className={styles.installButton}
          onClick={() => {
            void promptInstall();
          }}
        >
          {t('install.button')}
        </button>
      </div>
    );
  }

  if (isIosSafari()) {
    return (
      <div className={styles.installWrap}>
        <p className={styles.iosHint}>
          {t('install.iosHintLead')}<strong>{t('install.iosHintAction')}</strong>.
        </p>
      </div>
    );
  }

  return null;
}
